import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  getBasculas,
  getUsuariosTodos,
  asignarBasculaAUsuario,
  desasignarBasculaDeUsuario,
} from 'src/services/axiomaService'
import './AxiomaBasculasContainer.css'

const AxiomaBasculasContainer = () => {
  const [basculas, setBasculas] = useState([])
  const [usuarios, setUsuarios] = useState([])
  const [basculasAsignadas, setBasculasAsignadas] = useState({}) // { usuarioId: basculaId }
  const [loading, setLoading] = useState(true)
  const [isDragging, setIsDragging] = useState(false)
  const scrollIntervalRef = useRef(null)
  const containerRef = useRef(null)

  const cargarDatos = useCallback(async () => {
    setLoading(true)
    try {
      const [basculasData, usuariosData] = await Promise.all([
        getBasculas(),
        getUsuariosTodos(),
      ])

      // Procesar básculas
      if (basculasData) {
        if (basculasData.success && Array.isArray(basculasData.success)) {
          setBasculas(basculasData.success)
        } else if (Array.isArray(basculasData)) {
          setBasculas(basculasData)
        }
      }

      // Procesar usuarios - la respuesta puede venir en diferentes formatos
      let usuariosList = []
      if (usuariosData) {
        if (Array.isArray(usuariosData)) {
          usuariosList = usuariosData
        } else if (usuariosData.success && Array.isArray(usuariosData.success)) {
          usuariosList = usuariosData.success
        } else if (usuariosData.data && Array.isArray(usuariosData.data)) {
          usuariosList = usuariosData.data
        }
      }

      setUsuarios(usuariosList)
      
      // Mapear las básculas asignadas
      const asignadas = {}
      usuariosList.forEach((usuario) => {
        if (usuario.bascula_id) {
          asignadas[usuario.id] = usuario.bascula_id
        }
      })
      setBasculasAsignadas(asignadas)
    } catch (error) {
      console.error('Error al cargar datos:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    cargarDatos()
  }, [cargarDatos])

  // Auto-scroll durante el drag
  useEffect(() => {
    if (!isDragging) {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current)
        scrollIntervalRef.current = null
      }
      return
    }

    const handleGlobalDragOver = (e) => {
      const scrollThreshold = 100 // Distancia desde el borde para activar scroll
      const scrollSpeed = 10 // Velocidad de scroll
      const viewportHeight = window.innerHeight
      const mouseY = e.clientY

      // Detectar si está cerca del borde superior
      if (mouseY < scrollThreshold) {
        if (!scrollIntervalRef.current) {
          scrollIntervalRef.current = setInterval(() => {
            window.scrollBy(0, -scrollSpeed)
          }, 16) // ~60fps
        }
      }
      // Detectar si está cerca del borde inferior
      else if (mouseY > viewportHeight - scrollThreshold) {
        if (!scrollIntervalRef.current) {
          scrollIntervalRef.current = setInterval(() => {
            window.scrollBy(0, scrollSpeed)
          }, 16) // ~60fps
        }
      }
      // Si está en el medio, detener el scroll
      else if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current)
        scrollIntervalRef.current = null
      }
    }

    document.addEventListener('dragover', handleGlobalDragOver)

    return () => {
      document.removeEventListener('dragover', handleGlobalDragOver)
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current)
        scrollIntervalRef.current = null
      }
    }
  }, [isDragging])

  const obtenerBasculasNoAsignadas = () => {
    const basculasAsignadasIds = Object.values(basculasAsignadas)
    return basculas.filter((bascula) => !basculasAsignadasIds.includes(bascula.id))
  }

  const obtenerBasculaAsignadaAUsuario = (usuarioId) => {
    const basculaId = basculasAsignadas[usuarioId]
    if (!basculaId) return null
    return basculas.find((b) => b.id === basculaId)
  }

  const handleDragStart = (e, bascula) => {
    e.dataTransfer.setData('bascula', JSON.stringify(bascula))
    e.dataTransfer.effectAllowed = 'move'
    setIsDragging(true)
  }

  const handleDragEnd = () => {
    setIsDragging(false)
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current)
      scrollIntervalRef.current = null
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    e.currentTarget.classList.add('drag-over')
  }

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over')
  }

  const handleDropEnUsuario = async (e, usuarioId) => {
    e.preventDefault()
    setIsDragging(false)
    try {
      const basculaData = JSON.parse(e.dataTransfer.getData('bascula'))
      const basculaId = basculaData.id

      // Si el usuario ya tiene una báscula asignada, desasignarla primero
      const basculaAnteriorId = basculasAsignadas[usuarioId]
      if (basculaAnteriorId) {
        await desasignarBasculaDeUsuario(usuarioId, basculaAnteriorId)
      }

      // Asignar la nueva báscula
      const resultado = await asignarBasculaAUsuario(usuarioId, basculaId)
      if (resultado) {
        setBasculasAsignadas((prev) => ({
          ...prev,
          [usuarioId]: basculaId,
        }))
        // Recargar datos para asegurar consistencia
        await cargarDatos()
      }
    } catch (error) {
      console.error('Error al asignar báscula:', error)
    }
  }

  const handleDropEnAreaLibre = async (e, basculaId) => {
    e.preventDefault()
    setIsDragging(false)
    try {
      // Encontrar el usuario que tiene esta báscula asignada
      const usuarioId = Object.keys(basculasAsignadas).find(
        (uid) => basculasAsignadas[uid] === basculaId,
      )

      if (usuarioId) {
        const resultado = await desasignarBasculaDeUsuario(usuarioId, basculaId)
        if (resultado) {
          setBasculasAsignadas((prev) => {
            const nuevo = { ...prev }
            delete nuevo[usuarioId]
            return nuevo
          })
          // Recargar datos para asegurar consistencia
          await cargarDatos()
        }
      }
    } catch (error) {
      console.error('Error al desasignar báscula:', error)
    }
  }

  const handleEliminarAsignacion = async (usuarioId) => {
    try {
      const basculaId = basculasAsignadas[usuarioId]
      if (!basculaId) return

      const resultado = await desasignarBasculaDeUsuario(usuarioId, basculaId)
      if (resultado) {
        setBasculasAsignadas((prev) => {
          const nuevo = { ...prev }
          delete nuevo[usuarioId]
          return nuevo
        })
        await cargarDatos()
      }
    } catch (error) {
      console.error('Error al eliminar asignación:', error)
    }
  }

  if (loading) {
    return (
      <div className="basculas-container">
        <div className="basculas-loading">Cargando...</div>
      </div>
    )
  }

  const basculasNoAsignadas = obtenerBasculasNoAsignadas()

  return (
    <div className="basculas-container" ref={containerRef}>
      <div className="basculas-header">
        <h1 className="basculas-title">Gestión de Básculas</h1>
        <p className="basculas-subtitle">
          Arrastra las básculas para asignarlas a los usuarios
        </p>
      </div>

      {/* Área de básculas no asignadas */}
      <div className="basculas-libres-section">
        <h2 className="basculas-section-title">Básculas Disponibles</h2>
        <div
          className="basculas-libres-container"
          onDragOver={handleDragOver}
          onDrop={(e) => {
            const basculaData = JSON.parse(e.dataTransfer.getData('bascula'))
            handleDropEnAreaLibre(e, basculaData.id)
          }}
        >
          {basculasNoAsignadas.length > 0 ? (
            basculasNoAsignadas.map((bascula) => (
              <div
                key={bascula.id}
                className="bascula-item bascula-libre"
                draggable
                onDragStart={(e) => handleDragStart(e, bascula)}
                onDragEnd={handleDragEnd}
              >
                <span className="bascula-nombre">{bascula.nombre}</span>
              </div>
            ))
          ) : (
            <p className="basculas-empty">No hay básculas disponibles</p>
          )}
        </div>
      </div>

      {/* Cards de usuarios */}
      <div className="usuarios-section">
        <h2 className="basculas-section-title">Usuarios</h2>
        {usuarios.length === 0 ? (
          <p className="basculas-empty">No hay usuarios disponibles</p>
        ) : (
          <div className="usuarios-grid">
            {usuarios.map((usuario) => {
            const basculaAsignada = obtenerBasculaAsignadaAUsuario(usuario.id)
            return (
              <div
                key={usuario.id}
                className="usuario-card"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => {
                  handleDropEnUsuario(e, usuario.id)
                  e.currentTarget.classList.remove('drag-over')
                }}
              >
                <div className="usuario-card-header">
                  <h3 className="usuario-nombre">
                    {usuario.nombre} {usuario.apellido}
                  </h3>
                  <p className="usuario-correo">{usuario.correo}</p>
                </div>
                <div className="usuario-bascula-area">
                  {basculaAsignada ? (
                    <div className="bascula-item bascula-asignada">
                      <span className="bascula-nombre">{basculaAsignada.nombre}</span>
                      <button
                        type="button"
                        className="bascula-eliminar"
                        onClick={() => handleEliminarAsignacion(usuario.id)}
                        title="Eliminar asignación"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="bascula-placeholder">
                      Arrastra una báscula aquí
                    </div>
                  )}
                </div>
              </div>
            )
          })}
          </div>
        )}
      </div>
    </div>
  )
}

export default AxiomaBasculasContainer
